import React, { useCallback } from 'react';
import { Formik, useFormikContext, Form } from 'formik';
import { observer } from 'mobx-react';
import { Button, Grid } from '@material-ui/core';

import { useStore } from 'core/store';
import Field from 'core/components/form/form.v2';
import { Label } from 'core/components/label';
import { track } from 'core/helpers/analytics';
import { useToast } from 'core/components/toast';
import removeIcon from 'core/assets/svg/remove.svg';

import useStyles from './farm-association-form.styles';

const validate = (formValues, onError = () => {}) => {
  const errors = {};
  if (!formValues.memberOfCattlemenAssociation && !formValues.memberOfSoybeanAssociation) {
    errors.memberOfCattlemenAssociation = 'At least one association must be selected';
    errors.memberOfSoybeanAssociation = 'At least one association must be selected';
    onError();
  }
  return errors;
};

const FarmAssociationForm = observer(() => {
  const { offer: offerStore } = useStore();
  const { setFieldValue, setFieldTouched, values } = useFormikContext();
  const classes = useStyles();
  const toast = useToast();

  const state = offerStore.offer?.quote?.correctedAddress?.state;

  const handleSubmit = (modalValues) => {
    offerStore.setIncludeAssociationDiscount(true);
    offerStore.setShowFarmAssociationModal(false);
    setFieldValue('global.farmAssociationDiscount', true);
    setFieldTouched('global.farmAssociationDiscount');
    setFieldValue('global.memberOfCattlemenAssociation', modalValues.memberOfCattlemenAssociation);
    setFieldTouched('global.memberOfCattlemenAssociation');
    setFieldValue('global.memberOfSoybeanAssociation', modalValues.memberOfSoybeanAssociation);
    setFieldTouched('global.memberOfSoybeanAssociation');
    toast.notify({
      type: 'success',
      message: 'The MO Farm Associations discount has been added.'
    });
    track('Association Discount Added', {
      discount: 'MO Farm Associations',
      offerId: offerStore.offer?.id,
      state
    });
  };

  const onRemoveClick = useCallback(() => {
    offerStore.setIncludeAssociationDiscount(false);
    offerStore.setShowFarmAssociationModal(false);
    setFieldValue('global.farmAssociationDiscount', false);
    setFieldTouched('global.farmAssociationDiscount');
    setFieldValue('global.memberOfCattlemenAssociation', false);
    setFieldTouched('global.memberOfCattlemenAssociation');
    setFieldValue('global.memberOfSoybeanAssociation', false);
    setFieldTouched('global.memberOfSoybeanAssociation');
    toast.notify({
      type: 'success',
      message: 'The MO Farm Associations discount has been removed.'
    });
    track('Association Discount Removed', {
      discount: 'MO Farm Associations',
      offerId: offerStore.offer?.id,
      state
    });
  }, [setFieldValue, setFieldTouched, offerStore, toast, state]);

  return (
    <Formik
      initialValues={{
        memberOfCattlemenAssociation: values?.global?.memberOfCattlemenAssociation ?? false,
        memberOfSoybeanAssociation: values?.global?.memberOfSoybeanAssociation ?? false
      }}
      onSubmit={handleSubmit}
      validate={(formValues) =>
        validate(formValues, () =>
          toast.notify({
            type: 'error',
            message: 'Please complete the required form items to continue.'
          })
        )
      }
      enableReinitialize
    >
      <Form>
        <Field
          xs={12}
          type="checkbox"
          id="memberOfCattlemenAssociation"
          name="memberOfCattlemenAssociation"
          label="Cattlemen's Association"
          mode="light"
        />
        <Field
          xs={12}
          type="checkbox"
          id="memberOfSoybeanAssociation"
          name="memberOfSoybeanAssociation"
          label="Soybean Association"
          mode="light"
        />
        <Grid container direction="row" justifyContent="flex-end" className={classes.buttonContainer}>
          {offerStore?.includeAssociationDiscount && (
            <Grid item>
              <Button
                onClick={onRemoveClick}
                disabled={false}
                variant="text"
                color="secondary"
                className={classes.removeDiscountBtn}
              >
                <img alt="trashcan icon" src={removeIcon} className={classes.removeIcon} />
                <Label variant="body3" className={classes.removeLabel}>
                  Remove discount
                </Label>
              </Button>
            </Grid>
          )}
          <Grid item>
            <Button className={classes.applyDiscountBtn} variant="contained" color="primary" type="submit">
              Apply discount
            </Button>
          </Grid>
        </Grid>
      </Form>
    </Formik>
  );
});

export default FarmAssociationForm;
